import { useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Search, ClipboardPaste, Camera, ExternalLink, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

/**
 * 3-tab modal: Smart Search / Paste Text / Image Upload.
 * On success calls onImported(courseObject) with shape:
 *   { course_name, num_holes, tees: [{name, color, holes:[{hole,par,yardage,hcp?}], total_yardage, total_par, course_rating?, slope?}] }
 * which is what AdminPanel's review dialog already expects.
 */
export default function CourseImportDialog({ open, onOpenChange, onImported }) {
  const [tab, setTab] = useState('url');
  // --- URL Import ---
  const [importUrl, setImportUrl] = useState('');
  const [importingUrl, setImportingUrl] = useState(false);
  // --- Smart Search ---
  const [searchName, setSearchName] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searching, setSearching] = useState(false);
  // --- Paste Text ---
  const [pasteText, setPasteText] = useState('');
  const [parsing, setParsing] = useState(false);
  // --- Image ---
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);

  const resetAll = () => {
    setImportUrl('');
    setSearchName(''); setSearchCity(''); setSearchState('');
    setPasteText('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const finish = (course) => {
    onImported(course);
    onOpenChange(false);
    resetAll();
  };

  const handleImportUrl = async () => {
    const u = importUrl.trim();
    if (!u.startsWith('http')) {
      toast.error('Paste the full URL (https://…)');
      return;
    }
    setImportingUrl(true);
    try {
      const res = await axios.post(`${API}/courses/import-url`, { url: u });
      toast.success('Scorecard imported! Verify the values before saving.');
      finish(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'URL import failed. Try Paste Text.';
      toast.error(msg);
    } finally {
      setImportingUrl(false);
    }
  };

  const handleSmartSearch = async () => {
    if (searchName.trim().length < 3) {
      toast.error('Enter the course name');
      return;
    }
    setSearching(true);
    try {
      const res = await axios.post(`${API}/courses/search-online`, {
        course_name: searchName.trim(),
        city: searchCity.trim(),
        state: searchState.trim(),
      });
      toast.success('Course found! Verify the values before saving.');
      finish(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Online search failed. Try Paste Text or Image.';
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  const handleParseText = async () => {
    if (pasteText.trim().length < 40) {
      toast.error('Paste the full scorecard text (par, yardage, all 18 holes).');
      return;
    }
    setParsing(true);
    try {
      const res = await axios.post(`${API}/courses/parse-text`, { text: pasteText });
      toast.success('Scorecard parsed! Verify the values before saving.');
      finish(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not parse the text.';
      toast.error(msg);
    } finally {
      setParsing(false);
    }
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      const res = await axios.post(`${API}/courses/scan`, { image_base64: base64 });
      toast.success('Scorecard scanned! Verify the values before saving.');
      finish(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Scan failed. Try a clearer photo.';
      toast.error(msg);
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!searching && !parsing && !scanning && !importingUrl) onOpenChange(v); }}>
      <DialogContent className="max-w-lg p-4 sm:p-6" data-testid="course-import-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>Import Course</DialogTitle>
          <DialogDescription>Pick the easiest source. You can review and edit before saving.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-[#E8E9E3]">
            <TabsTrigger value="url" data-testid="import-tab-url">
              <LinkIcon className="h-4 w-4 mr-1" />URL
            </TabsTrigger>
            <TabsTrigger value="search" data-testid="import-tab-search">
              <Search className="h-4 w-4 mr-1" />Search
            </TabsTrigger>
            <TabsTrigger value="paste" data-testid="import-tab-paste">
              <ClipboardPaste className="h-4 w-4 mr-1" />Paste
            </TabsTrigger>
            <TabsTrigger value="image" data-testid="import-tab-image">
              <Camera className="h-4 w-4 mr-1" />Image
            </TabsTrigger>
          </TabsList>

          {/* URL IMPORT */}
          <TabsContent value="url" className="space-y-3 mt-4">
            <p className="text-xs text-[#6B6E66]">
              Paste the direct URL to the scorecard page (BlueGolf, club website, GolfNow, etc.).
              AI will open the page and extract the scorecard — even when the site blocks normal scraping.
            </p>
            <div>
              <Label className="text-[#1B3C35]">Scorecard URL *</Label>
              <Input
                value={importUrl}
                onChange={e => setImportUrl(e.target.value)}
                placeholder="https://course.bluegolf.com/.../detailedscorecard.htm"
                className="mt-1 border-[#E2E3DD] h-11 text-xs"
                data-testid="import-url-input"
                autoFocus
              />
            </div>
            <Button onClick={handleImportUrl} disabled={importingUrl}
              className="w-full h-11 bg-[#1B3C35] hover:bg-[#1B3C35]/90 text-white"
              data-testid="import-url-submit">
              {importingUrl ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reading page (10-30s)...</> :
                <><LinkIcon className="h-4 w-4 mr-2" />Read URL & Import</>}
            </Button>
            <p className="text-[10px] text-[#6B6E66] text-center">
              Tip: For BlueGolf use the <code className="px-1 bg-[#E8E9E3] rounded">detailedscorecard.htm</code> link.
            </p>
          </TabsContent>

          {/* SMART SEARCH */}
          <TabsContent value="search" className="space-y-3 mt-4">
            <p className="text-xs text-[#6B6E66]">
              AI searches the web (BlueGolf / club site / GolfNow) for the scorecard.
              Add the city / state for a more accurate match.
            </p>
            <div>
              <Label className="text-[#1B3C35]">Course Name *</Label>
              <Input value={searchName} onChange={e => setSearchName(e.target.value)}
                placeholder="e.g. Horn Rapids Golf Course"
                className="mt-1 border-[#E2E3DD] h-11" data-testid="import-search-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[#1B3C35]">City</Label>
                <Input value={searchCity} onChange={e => setSearchCity(e.target.value)}
                  placeholder="Richland" className="mt-1 border-[#E2E3DD] h-11" data-testid="import-search-city" />
              </div>
              <div>
                <Label className="text-[#1B3C35]">State / Country</Label>
                <Input value={searchState} onChange={e => setSearchState(e.target.value)}
                  placeholder="WA" className="mt-1 border-[#E2E3DD] h-11" data-testid="import-search-state" />
              </div>
            </div>
            <Button onClick={handleSmartSearch} disabled={searching}
              className="w-full h-11 bg-[#1B3C35] hover:bg-[#1B3C35]/90 text-white"
              data-testid="import-search-submit">
              {searching ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Searching the web...</> :
                <><Search className="h-4 w-4 mr-2" />Search & Import</>}
            </Button>
          </TabsContent>

          {/* PASTE TEXT */}
          <TabsContent value="paste" className="space-y-3 mt-4">
            <p className="text-xs text-[#6B6E66]">
              Copy the scorecard from the course's site (BlueGolf, club page, email) and paste it here.
              Works when the site blocks scraping.
              <a href="https://www.bluegolf.com/" target="_blank" rel="noopener noreferrer"
                className="ml-1 inline-flex items-center text-[#1B3C35] underline">
                BlueGolf <ExternalLink className="h-3 w-3 ml-0.5" />
              </a>
            </p>
            <Textarea
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              placeholder={`Paste raw scorecard text here.\nMust include hole numbers, par and yardage rows.\n\nExample:\nHole  1  2  3 ...\nPar   4  3  5 ...\nBlack 415 198 540 ...`}
              rows={9}
              className="border-[#E2E3DD] text-xs font-mono leading-snug"
              data-testid="import-paste-text" />
            <div className="text-[10px] text-[#6B6E66] tabular-nums">
              {pasteText.length.toLocaleString()} chars
            </div>
            <Button onClick={handleParseText} disabled={parsing}
              className="w-full h-11 bg-[#1B3C35] hover:bg-[#1B3C35]/90 text-white"
              data-testid="import-paste-submit">
              {parsing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Parsing...</> :
                <><ClipboardPaste className="h-4 w-4 mr-2" />Parse & Import</>}
            </Button>
          </TabsContent>

          {/* IMAGE */}
          <TabsContent value="image" className="space-y-3 mt-4">
            <p className="text-xs text-[#6B6E66]">
              Snap or upload a photo of the scorecard. Best results: flat card, holes left → right,
              good light, no glare.
            </p>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImagePick}
              className="hidden" data-testid="import-image-file" />
            <Button onClick={() => fileRef.current?.click()} disabled={scanning}
              className="w-full h-11 bg-[#C96A52] hover:bg-[#C96A52]/90 text-white"
              data-testid="import-image-submit">
              {scanning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Scanning...</> :
                <><Camera className="h-4 w-4 mr-2" />Choose Photo & Scan</>}
            </Button>
            <p className="text-[10px] text-[#6B6E66] text-center">
              AI may misread numbers — you'll get a chance to fix them.
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
